/**
 * TGRM "Yard Mate" — storefront widget
 * ------------------------------------
 * Drop-in chat widget for the TGRM Shopify theme. No build step, no dependencies.
 * It talks ONLY to your own Worker backend (set window.YARDMATE_ENDPOINT), so no
 * AI key is ever exposed on the website.
 *
 * v2 changes:
 *  - Conversation persists across page loads (sessionStorage, per browser tab session)
 *  - Thumbs up/down feedback on bot answers (fire-and-forget POST {type:"feedback"})
 *  - Mobile: launcher/panel sit above the TGRM call bar
 *  - esc() also escapes quotes; history sent to backend capped at last 24 messages
 */
(function () {
  "use strict";
  var ENDPOINT = window.YARDMATE_ENDPOINT || "";
  if (!ENDPOINT) { console.warn("[YardMate] window.YARDMATE_ENDPOINT not set"); return; }

  var STARTERS = [
    "I've got about 5km of fence and no mains power — what do I need?",
    "Compare two energisers for me",
    "Which knapsack sprayer for a small property?",
    "What do I need to set up a new electric fence?"
  ];

  var STORE_KEY = "ym_session_v1";
  var HISTORY_CAP = 24;   // messages sent to backend per request

  var history = [];     // {role, content}
  var catalogue = {};   // sku -> product (from latest response)

  /* ---------- styles ---------- */
  var css = `
  .ym-launch{position:fixed;right:22px;bottom:22px;z-index:2147483000;width:46px;height:46px;border-radius:18px;
    border:3px solid #fff;cursor:pointer;background:linear-gradient(160deg,#FF8A1E,#F4520B);box-shadow:0 14px 30px -6px rgba(244,82,11,.6),0 0 0 0 rgba(255,138,30,.55);
    display:grid;place-items:center;transition:transform .15s;animation:ympulse 2.4s ease-out infinite}
  .ym-launch:hover{transform:translateY(-3px) scale(1.04)}
  .ym-launch svg{width:32px;height:32px}
  @keyframes ympulse{0%{box-shadow:0 14px 30px -6px rgba(244,82,11,.6),0 0 0 0 rgba(255,138,30,.55)}70%{box-shadow:0 14px 30px -6px rgba(244,82,11,.6),0 0 0 18px rgba(255,138,30,0)}100%{box-shadow:0 14px 30px -6px rgba(244,82,11,.6),0 0 0 0 rgba(255,138,30,0)}}
  .ym-nudge{position:fixed;right:96px;bottom:42px;z-index:2147483000;max-width:230px;background:#fff;color:#23402d;
    border:2px solid #F4520B;border-radius:16px;border-bottom-right-radius:4px;padding:12px 32px 12px 14px;font-size:14px;line-height:1.4;font-weight:600;
    box-shadow:0 12px 28px -10px rgba(28,40,24,.5);font-family:Inter,-apple-system,system-ui,Segoe UI,Roboto,sans-serif;
    cursor:pointer;display:none;animation:ymnudge .35s ease-out}
  .ym-nudge.show{display:block}
  @keyframes ymnudge{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .ym-nudge-x{position:absolute;top:4px;right:7px;border:none;background:transparent;color:#9aa396;font-size:17px;line-height:1;cursor:pointer;padding:2px}
  .ym-panel{position:fixed;right:20px;bottom:20px;z-index:2147483001;width:294px;max-width:calc(100vw - 24px);
    height:480px;max-height:calc(100vh - 24px);background:#EEF0E9;border:1px solid #cfd4c6;border-radius:18px;overflow:hidden;
    display:none;flex-direction:column;box-shadow:0 24px 60px -18px rgba(28,40,24,.5);
    font-family:Inter,-apple-system,system-ui,Segoe UI,Roboto,sans-serif;color:#1A1C18}
  .ym-panel.ym-open{display:flex}
  @media (max-width:767px){
    .ym-launch{bottom:78px}
    .ym-nudge{bottom:98px}
    .ym-panel{bottom:66px;max-height:calc(100vh - 80px)}
  }
  html.ym-pdp .ym-launch{bottom:96px}
  html.ym-pdp .ym-nudge{bottom:116px}
  @media (max-width:767px){
    html.ym-pdp .ym-launch{bottom:162px}
    html.ym-pdp .ym-nudge{bottom:182px}
    html.ym-pdp .ym-panel{bottom:150px;max-height:calc(100vh - 164px)}
  }
  .ym-head{background:linear-gradient(180deg,#23402d,#2f5239);color:#eef0e9;padding:13px 14px;display:flex;gap:11px;align-items:center;border-bottom:3px solid #C8852A}
  .ym-ava{width:38px;height:38px;border-radius:10px;background:#1b3322;display:grid;place-items:center;border:1px solid rgba(255,255,255,.14);flex:0 0 38px}
  .ym-brand{flex:1;min-width:0;line-height:1.1}
  .ym-co{font-size:11px;letter-spacing:.13em;text-transform:uppercase;color:#c5cdbb;font-weight:600}
  .ym-name{font-size:18px;font-weight:700;color:#fff}
  .ym-name small{font-weight:500;font-size:12px;color:#bcc6b2}
  .ym-x{background:transparent;border:none;color:#cdd6c6;cursor:pointer;font-size:20px;line-height:1;padding:4px 6px}
  .ym-scroll{flex:1;overflow-y:auto;padding:14px 12px 6px}
  .ym-row{display:flex;margin:0 0 11px;gap:8px;align-items:flex-end}
  .ym-row.ym-u{justify-content:flex-end}
  .ym-mini{width:24px;height:24px;flex:0 0 24px;border-radius:7px;background:#23402d;display:grid;place-items:center;margin-bottom:2px}
  .ym-bub{max-width:84%;padding:10px 12px;border-radius:14px;font-size:14px;line-height:1.5;white-space:pre-wrap;word-wrap:break-word}
  .ym-b .ym-bub{background:#FBFBF8;border:1px solid #e0e3d7;border-bottom-left-radius:5px}
  .ym-u .ym-bub{background:#C8852A;color:#fff;border-bottom-right-radius:5px}
  .ym-fb{display:flex;gap:5px;margin:-6px 0 11px 32px}
  .ym-fb button{border:1px solid #d4dac9;background:#fff;border-radius:14px;font-size:11px;padding:3px 9px;cursor:pointer;color:#5a6353;line-height:1.4}
  .ym-fb button:hover{border-color:#23402d;color:#23402d}
  .ym-fb button.on{background:#23402d;border-color:#23402d;color:#fff}
  .ym-fb button:disabled{cursor:default;opacity:.85}
  .ym-cards{display:flex;flex-direction:column;gap:8px;margin:0 0 11px;padding-left:32px}
  .ym-card{display:flex;gap:10px;background:#FBFBF8;border:1px solid #e0e3d7;border-radius:12px;padding:8px;align-items:center;text-decoration:none;color:inherit}
  .ym-card img{width:56px;height:56px;flex:0 0 56px;object-fit:contain;background:#fff;border-radius:8px;border:1px solid #eceee6}
  .ym-meta{flex:1;min-width:0}
  .ym-pt{font-weight:600;font-size:13.5px;line-height:1.2;margin:0 0 4px}
  .ym-tags{display:flex;flex-wrap:wrap;gap:4px;margin:3px 0}
  .ym-tag{font-size:10px;font-weight:600;padding:2px 7px;border-radius:20px;background:#eef0e8;color:#4a5446;border:1px solid #dfe3d7;white-space:nowrap}
  .ym-tag.pw{background:#e7efe8;color:#2f5239;border-color:#cfe0d2}
  .ym-prow{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:2px}
  .ym-price{font-weight:700;font-size:16px;color:#23402d}
  .ym-stk{font-size:10.5px;font-weight:600}
  .ym-stk.good{color:#3f7d4e}.ym-stk.out{color:#8d8d86}
  .ym-cmp{margin:0 0 11px;padding-left:32px}
  .ym-sheet{background:#FBFBF8;border:1px solid #d9ddcf;border-radius:12px;overflow:hidden}
  .ym-ctitle{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#56604f;font-weight:700;padding:8px 11px;background:#eef0e8;border-bottom:1px solid #e0e3d7}
  .ym-sheet table{border-collapse:collapse;width:100%;font-size:12px}
  .ym-sheet th,.ym-sheet td{padding:7px 8px;text-align:left;border-bottom:1px solid #edefe7;vertical-align:top}
  .ym-sheet thead th{background:#f4f5ef}
  .ym-sheet thead th.pick{background:#fbf2e3}
  .ym-cn{font-weight:600;display:block;font-size:12px;line-height:1.2}
  .ym-lab{font-weight:600;color:#5a6353;background:#fafbf7;width:84px}
  .ym-pick{background:#fdf7ec}
  .ym-rib{display:inline-block;background:#C8852A;color:#fff;font-size:8.5px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;padding:2px 6px;border-radius:20px;margin-bottom:3px}
  .ym-why{padding:8px 11px;font-size:12px;line-height:1.45;color:#41493d;background:#fbf6ed;border-top:1px solid #f0e6d3}
  .ym-why b{color:#A66A1C}
  .ym-chips{display:flex;flex-wrap:wrap;gap:6px;padding:0 12px 11px 44px}
  .ym-chip{font-size:12px;font-weight:500;color:#23402d;background:#fff;border:1px solid #d4dac9;border-radius:20px;padding:6px 11px;cursor:pointer;text-align:left}
  .ym-chip:hover{background:#23402d;color:#fff;border-color:#23402d}
  .ym-type span{display:inline-block;width:6px;height:6px;border-radius:50%;background:#9AA396;margin-right:3px;animation:ymb 1s infinite}
  .ym-status{display:block;font-style:normal;font-size:12px;color:#5a6353;margin-top:5px;font-weight:600}
  .ym-type span:nth-child(2){animation-delay:.15s}.ym-type span:nth-child(3){animation-delay:.3s}
  @keyframes ymb{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}
  .ym-comp{padding:10px 11px;border-top:1px solid #d9ddcf;background:#e7eadf;display:flex;gap:8px;align-items:flex-end}
  .ym-comp textarea{flex:1;resize:none;border:1px solid #cdd3c4;border-radius:11px;padding:9px 11px;font:inherit;font-size:14px;line-height:1.4;max-height:90px;background:#fff}
  .ym-comp textarea:focus{outline:2px solid #C8852A;border-color:#C8852A}
  .ym-send{flex:0 0 42px;width:42px;height:42px;border-radius:11px;border:none;background:#23402d;color:#fff;cursor:pointer;display:grid;place-items:center}
  .ym-send:disabled{background:#aeb6a6;cursor:not-allowed}
  .ym-foot{font-size:10px;color:#9AA396;text-align:center;padding:0 0 7px}
  .ym-more{display:block;margin:0 0 12px 32px;font-size:13px;font-weight:600;color:#C8852A;text-decoration:none;border:1px dashed #d9b483;border-radius:10px;padding:8px 11px;background:#fdf7ec}
  .ym-more:hover{background:#C8852A;color:#fff;border-style:solid}
  @media (prefers-reduced-motion:reduce){.ym-launch,.ym-type span{animation:none;transition:none}}
  `;

  var SPARK = '<svg viewBox="0 0 24 24" fill="none"><path d="M3 8h18M3 12h18M3 16h18" stroke="#6f8f78" stroke-width="1.4"/><path d="M13 2L7 13h4l-2 9 8-12h-4l2-8z" fill="#f0b341" stroke="#23402d" stroke-width=".6"/></svg>';
  var LAUNCH_ICON = '<svg viewBox="0 0 24 24" fill="none"><path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.65L3 20.5l1.4-5.6A8.5 8.5 0 1 1 21 11.5z" fill="#fff"/><path d="M12.6 5.5L8 13h3.2l-1.3 5.5L15 10h-3.2l1.3-4.5z" fill="#F4520B"/></svg>';

  /* ---------- session persistence ---------- */
  function saveSession() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        history: history,
        catalogue: catalogue,
        html: scroll ? scroll.innerHTML : "",
        open: panel ? panel.classList.contains("ym-open") : false
      }));
    } catch (e) {}
  }
  function loadSession() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || !s.history || !s.history.length) return null;
      return s;
    } catch (e) { return null; }
  }

  /* ---------- build DOM ---------- */
  var style = document.createElement("style"); style.textContent = css; document.head.appendChild(style);
  var ymFix = document.createElement("style");
ymFix.textContent = ".ym-panel{height:auto !important;max-height:min(85vh,540px) !important;width:min(92vw,480px) !important;}.ym-scroll{max-height:none !important;flex:1 1 auto !important;min-height:0 !important;overflow-y:auto !important;}";
document.head.appendChild(ymFix);

  var launch = h('button', 'ym-launch', LAUNCH_ICON); launch.setAttribute('aria-label', 'Open Yard Mate product helper');
  var nudge = document.createElement('div'); nudge.className = 'ym-nudge';
  nudge.innerHTML = 'G\u2019day! Ask me anything about our products \u2014 fencing, chemicals, water, livestock.<button class="ym-nudge-x" aria-label="Dismiss">&times;</button>';
  var panel = document.createElement('div'); panel.className = 'ym-panel';
  panel.innerHTML =
    '<div class="ym-head">'
    + '<div class="ym-ava">'+SPARK+'</div>'
    + '<div class="ym-brand"><div class="ym-co">Tom Grady Rural</div><div class="ym-name">Yard Mate <small>\u00b7 product helper</small></div></div>'
    + '<button class="ym-x" aria-label="Close">&times;</button></div>'
    + '<div class="ym-scroll"></div>'
    + '<div class="ym-chips"></div>'
    + '<div class="ym-comp"><textarea rows="1" placeholder="Ask me to compare or recommend\u2026"></textarea>'
    + '<button class="ym-send" aria-label="Send"><svg width="20" height="20" viewBox="0 0 24 24"><path d="M4 12l16-8-5 8 5 8-16-8z" fill="#fff"/></svg></button></div>'
    + '<div class="ym-foot">Answers from the live TGRM catalogue</div>';

  document.body.appendChild(launch);
  document.body.appendChild(nudge);
  document.body.appendChild(panel);

  var scroll = panel.querySelector('.ym-scroll');
  var chips = panel.querySelector('.ym-chips');
  var input = panel.querySelector('textarea');
  var sendBtn = panel.querySelector('.ym-send');
  var typingNode = null;
  var greeted = false;

  // Product pages carry sticky add-to-cart bars; lift the widget clear of them
  if (document.querySelector('form[action*="/cart/add"]')) {
    document.documentElement.classList.add('ym-pdp');
  }

  /* restore a previous session from this browser tab */
  var restored = loadSession();
  var nudgeDismissed = false;
  if (restored) {
    history = restored.history || [];
    catalogue = restored.catalogue || {};
    scroll.innerHTML = restored.html || "";
    greeted = true;
    nudgeDismissed = true;
    chips.style.display = 'none';
    wireFeedback();
    if (restored.open) { panel.classList.add('ym-open'); down(); }
  }

  // pop-up nudge: appear after a moment, dismiss on click/open
  setTimeout(function(){ if(!nudgeDismissed && !panel.classList.contains('ym-open') && !document.documentElement.classList.contains('ym-pdp')) nudge.classList.add('show'); }, 2800);
  nudge.addEventListener('click', function(e){ if(e.target.classList.contains('ym-nudge-x')){ nudgeDismissed=true; nudge.classList.remove('show'); return; } open(); });

  launch.addEventListener('click', open);
  panel.querySelector('.ym-x').addEventListener('click', function(){ panel.classList.remove('ym-open'); saveSession(); });
  sendBtn.addEventListener('click', go);
  input.addEventListener('keydown', function(e){ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); go(); }});
  input.addEventListener('input', function(){ input.style.height='auto'; input.style.height=Math.min(input.scrollHeight,90)+'px'; });

  function open(){
    nudgeDismissed = true; nudge.classList.remove('show');
    panel.classList.add('ym-open'); input.focus();
    if(!greeted){
      greeted = true;
      addBot({reply:"G'day! I'm Yard Mate. Tell me about your setup \u2014 what you're after, how much fence or what you're treating, whether you've got mains power \u2014 and I'll point you to the right gear and compare your options.", show_skus:[]});
      STARTERS.forEach(function(s){ var c=h('button','ym-chip',esc(s)); c.onclick=function(){ ask(s); }; chips.appendChild(c); });
    }
    saveSession();
  }
  function go(){ var v=input.value.trim(); if(v && !sendBtn.disabled) ask(v); }

  /* ---------- send / receive ---------- */
  function ask(text){
    addUser(text); history.push({role:'user', content:text});
    chips.style.display='none'; input.value=''; input.style.height='auto';
    busy(true); typing(true);
    fetch(ENDPOINT, {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ messages: history.slice(-HISTORY_CAP) })
    })
    .then(function(r){ return r.json(); })
    .then(function(data){
      (data.products||[]).forEach(function(p){ catalogue[p.sku]=p; });
      typing(false); addBot(data, true);
      history.push({role:'assistant', content: data.reply || ''});
      saveSession();
    })
    .catch(function(){
      typing(false);
      addBot({reply:"I can't reach the store just now \u2014 try again in a moment, or call us on (07) 5482 1692.", show_skus:[]});
    })
    .then(function(){ busy(false); input.focus(); });
  }

  /* ---------- feedback ---------- */
  function sendFeedback(rating, turnIndex){
    try {
      fetch(ENDPOINT, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ type:'feedback', rating: rating, turn: turnIndex, messages: history.slice(-HISTORY_CAP) })
      }).catch(function(){});
    } catch(e) {}
  }
  function feedbackBar(turnIndex){
    var bar = h('div','ym-fb','');
    bar.setAttribute('data-turn', String(turnIndex));
    var up = h('button','', '\uD83D\uDC4D Helpful');
    var down_ = h('button','', '\uD83D\uDC4E Not quite');
    bar.appendChild(up); bar.appendChild(down_);
    return bar;
  }
  function wireFeedback(){
    scroll.querySelectorAll('.ym-fb').forEach(function(bar){
      if (bar.getAttribute('data-wired')) return;
      bar.setAttribute('data-wired','1');
      var turnIndex = parseInt(bar.getAttribute('data-turn')||'0',10);
      var btns = bar.querySelectorAll('button');
      btns.forEach(function(b, i){
        b.addEventListener('click', function(){
          if (bar.getAttribute('data-done')) return;
          bar.setAttribute('data-done','1');
          b.classList.add('on');
          btns.forEach(function(x){ x.disabled = true; });
          sendFeedback(i===0 ? 'up' : 'down', turnIndex);
          saveSession();
        });
      });
    });
  }

  /* ---------- render ---------- */
  function addUser(t){ var r=h('div','ym-row ym-u',''); r.appendChild(h('div','ym-bub', esc(t))); scroll.appendChild(r); down(); saveSession(); }
  function addBot(data, withFeedback){
    var r=h('div','ym-row ym-b','');
    r.appendChild(h('div','ym-mini', SPARK));
    r.appendChild(h('div','ym-bub', esc(data.reply||'')));
    scroll.appendChild(r);
    if(data.comparison && data.comparison.skus){ scroll.appendChild(comparison(data.comparison)); }
    else if(data.show_skus && data.show_skus.length){ scroll.appendChild(cards(data.show_skus)); }
    if(data.search_url && (data.show_skus&&data.show_skus.length || data.comparison)){
      var more=document.createElement('a'); more.className='ym-more'; more.href=data.search_url; more.target='_blank'; more.rel='noopener noreferrer';
      more.innerHTML='Browse the full range for \u201c'+esc(data.search_term||'this')+'\u201d \u2192';
      scroll.appendChild(more);
    }
    if(withFeedback){ scroll.appendChild(feedbackBar(history.length)); wireFeedback(); }
    down(); saveSession();
  }
  function cards(skus){
    var wrap=h('div','ym-cards','');
    skus.forEach(function(sku){
      var p=catalogue[sku]; if(!p) return;
      var a=document.createElement('a'); a.className='ym-card'; a.href=p.url||'#'; a.target='_blank'; a.rel='noopener noreferrer';
      var tags='';
      if(p.pickup_only==='YES') tags+='<span class="ym-tag pw">Pickup only</span>';
      else if(p.local_delivery==='YES') tags+='<span class="ym-tag pw">Local delivery</span>';
      var stock = p.available ? '<span class="ym-stk good">In stock</span>' : '<span class="ym-stk out">Out of stock</span>';
      a.innerHTML =
        (p.image?'<img src="'+esc(p.image)+'" alt="'+esc(p.title)+'" loading="lazy">':'')
        + '<div class="ym-meta"><div class="ym-pt">'+esc(p.title)+'</div>'
        + '<div class="ym-tags">'+tags+'</div>'
        + '<div class="ym-prow"><span class="ym-price">\$'+money(p.price)+'</span>'+stock+'</div></div>';
      wrap.appendChild(a);
    });
    return wrap;
  }
  function comparison(c){
    var cols=(c.skus||[]).map(function(s){return catalogue[s];}).filter(Boolean);
    var wrap=h('div','ym-cmp','');
    if(cols.length<2){ return cards(c.skus||[]); }
    var head='<th></th>'+cols.map(function(p){
      var pick=p.sku===c.recommended_sku;
      return '<th class="'+(pick?'pick':'')+'">'+(pick?'<span class="ym-rib">Pick of the paddock</span><br>':'')
        +'<span class="ym-cn">'+esc(p.title)+'</span></th>';
    }).join('');
    var rows=(c.rows||[]).filter(function(rw){
      var lab=String(rw&&rw.label||'').toLowerCase();
      return !/price|stock|freight|ship|deliver|cost|\$/.test(lab);
    }).map(function(rw){
      var cells=cols.map(function(p,i){ return '<td class="'+(p.sku===c.recommended_sku?'ym-pick':'')+'">'+esc((rw.values&&rw.values[i])||'\u2014')+'</td>'; }).join('');
      return '<tr><td class="ym-lab">'+esc(rw.label)+'</td>'+cells+'</tr>';
    }).join('');
    var priceRow='<tr><td class="ym-lab">Price</td>'+cols.map(function(p){return '<td class="'+(p.sku===c.recommended_sku?'ym-pick':'')+'"><b style="color:#23402d">\$'+money(p.price)+'</b></td>';}).join('')+'</tr>';
    var stockRow='<tr><td class="ym-lab">Stock</td>'+cols.map(function(p){return '<td class="'+(p.sku===c.recommended_sku?'ym-pick':'')+'">'+(p.available?'In stock':'Out of stock')+'</td>';}).join('')+'</tr>';
    wrap.innerHTML='<div class="ym-sheet"><div class="ym-ctitle">Side-by-side</div><table><thead><tr>'+head+'</tr></thead><tbody>'+rows+priceRow+stockRow+'</tbody></table>'
      +(c.why?'<div class="ym-why"><b>Our pick:</b> '+esc(c.why)+'</div>':'')+'</div>';
    return wrap;
  }

  var STATUS = ["Checking the catalogue\u2026", "Lining up your options\u2026", "Sorting the best fit\u2026"];
  var statusTimer = null;
  function typing(on){
    if(on){ typingNode=h('div','ym-row ym-b',''); typingNode.appendChild(h('div','ym-mini',SPARK));
      var bub=h('div','ym-bub ym-type','<span></span><span></span><span></span><i class="ym-status">'+STATUS[0]+'</i>');
      typingNode.appendChild(bub); scroll.appendChild(typingNode); down();
      var i=0; var st=bub.querySelector('.ym-status');
      statusTimer=setInterval(function(){ i=(i+1)%STATUS.length; if(st) st.textContent=STATUS[i]; }, 1400);
    }
    else { if(statusTimer){ clearInterval(statusTimer); statusTimer=null; } if(typingNode){ typingNode.remove(); typingNode=null; } }
  }
  function busy(b){ sendBtn.disabled=b; input.disabled=b; }
  function down(){ scroll.scrollTop=scroll.scrollHeight; }

  /* ---------- helpers ---------- */
  function h(tag,cls,html){ var e=document.createElement(tag); if(cls)e.className=cls; if(html!=null)e.innerHTML=html; return e; }
  function esc(s){ return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];}); }
  function money(n){ var x=Number(n||0); return x.toFixed(2); }
})();
